import React, { useState, useEffect, useRef } from 'react';
import { 
  List,
  useDataProvider,
  useNotify,
  Loading
} from 'react-admin';
import { 
  Box, 
  Paper, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Button
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { apiUrl } from '../../config/api';
import { httpClient } from '../../utils/httpClient';
import { AnalyticsCharts } from './AnalyticsCharts';

interface ContentAnalyticsItem {
  id: string;
  title: string;
  category_name: string | null;
  content_type_name: string | null;
  total_plays: number;
  total_likes: number;
  total_downloads: number;
  avg_completion_rate: number;
  total_returns: number;
}

interface AnalyticsResponse {
  success: boolean;
  data: {
    analytics: ContentAnalyticsItem[];
  };
}

const ContentAnalyticsList = () => {
  const [analytics, setAnalytics] = useState<ContentAnalyticsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'plays' | 'likes' | 'downloads' | 'completion_rate' | 'returns'>('plays');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const scrollPositionRef = useRef<number>(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  const handleSort = (field: 'plays' | 'likes' | 'downloads' | 'completion_rate' | 'returns', event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    scrollPositionRef.current = currentScroll;
    
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {

      setSortBy(field);
      setSortOrder('desc');
    }
    

    setPage(0);

    requestAnimationFrame(() => {
      window.scrollTo({ top: currentScroll, behavior: 'auto' });
    });
    setTimeout(() => {
      window.scrollTo({ top: currentScroll, behavior: 'auto' });
    }, 0);
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Export to CSV function
  const exportToCSV = () => {
    if (analytics.length === 0) {
      notify('No data to export', { type: 'warning' });
      return;
    }


    const formatCell = (value: any): string => {
      if (value === null || value === undefined) {
        return '""';
      }
      const cellStr = String(value);

      return `"${cellStr.replace(/"/g, '""')}"`;
    };

    const separator = ';';


    const headers = ['Name', 'Category', 'Type', 'Plays', 'Likes', 'Downloads', 'Completion %', 'Returns'];
    const headerRow = headers.map(h => formatCell(h)).join(separator);

    const rows = analytics.map(item => {
      return [
        formatCell(item.title || ''),
        formatCell(item.category_name || ''),
        formatCell(item.content_type_name || ''),
        formatCell(item.total_plays || 0),
        formatCell(item.total_likes || 0),
        formatCell(item.total_downloads || 0),
        formatCell(
          item.avg_completion_rate !== undefined && item.avg_completion_rate !== null
            ? `${Number(item.avg_completion_rate).toFixed(1)}%`
            : '0%'
        ),
        formatCell(item.total_returns || 0)
      ].join(separator);
    });


    const csvContent = [headerRow, ...rows].join('\r\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `content_analytics_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notify('Data exported successfully', { type: 'success' });
  };

  // Load categories and content types for filters
  const [categories, setCategories] = useState<any[]>([]);
  const [contentTypes, setContentTypes] = useState<any[]>([]);

  useEffect(() => {
    // Load categories
    dataProvider.getList('categories', {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: {}
    }).then(({ data }) => {
      setCategories(data);
    }).catch(() => {});

    // Load content types
    dataProvider.getList('content_types', {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'display_name', order: 'ASC' },
      filter: {}
    }).then(({ data }) => {
      setContentTypes(data);
    }).catch(() => {});
  }, [dataProvider]);

  // Load analytics data
  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams();
        if (categoryFilter) params.append('category_id', categoryFilter);
        if (contentTypeFilter) params.append('content_type_id', contentTypeFilter);
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);

        const url = `${apiUrl}/admin/analytics/content?${params.toString()}`;
        const response = await httpClient(url);
        
        if (response.json.success) {
          setAnalytics(response.json.data.analytics);
        } else {
          setError(response.json.message || 'Failed to load analytics');
          notify(response.json.message || 'Failed to load analytics', { type: 'error' });
        }
      } catch (err: any) {
        const errorMessage = (err instanceof Error && err.message) ? err.message : 'Failed to load analytics';
        setError(errorMessage);
        notify(errorMessage, { type: 'error' });
      } finally {
        setLoading(false);
        
        // Restore scroll position after loading completes
        const savedScroll = scrollPositionRef.current;
        if (savedScroll > 0) {
          requestAnimationFrame(() => {
            window.scrollTo({ top: savedScroll, behavior: 'auto' });
          });
          setTimeout(() => {
            window.scrollTo({ top: savedScroll, behavior: 'auto' });
          }, 10);
          setTimeout(() => {
            window.scrollTo({ top: savedScroll, behavior: 'auto' });
          }, 50);
        }
      }
    };

    loadAnalytics();
  }, [categoryFilter, contentTypeFilter, sortBy, sortOrder, notify]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Paper sx={{ padding: 2 }}>
          <Typography color="error">Error: {error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <List 
      title="Content Analytics" 
      empty={false} 
      disableSyncWithLocation
      resource="content_analytics"
      perPage={1000}
      sort={{ field: 'id', order: 'ASC' }}
      exporter={false}
    >
      <Box sx={{ padding: 2 }}>
        {/* Filters */}
        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={contentTypeFilter}
                  label="Content Type"
                  onChange={(e) => setContentTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {contentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.display_name || type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>

        {/* Charts */}
        <AnalyticsCharts data={analytics} />

        {/* Table */}
        <Paper sx={{ marginTop: 3 }}>
          <Box sx={{ padding: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <Typography variant="h6">
                Content Analytics
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportToCSV}
                disabled={analytics.length === 0}
              >
                Export to CSV
              </Button>
            </Box>
            <TableContainer ref={tableContainerRef}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'plays'}
                        direction={sortBy === 'plays' ? sortOrder : 'asc'}
                        onClick={(e) => handleSort('plays', e)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: '1 !important',
                            visibility: 'visible !important',
                          },
                          cursor: 'pointer',
                        }}
                        component="div"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <strong>Plays</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'likes'}
                        direction={sortBy === 'likes' ? sortOrder : 'asc'}
                        onClick={(e) => handleSort('likes', e)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: '1 !important',
                            visibility: 'visible !important',
                          },
                          cursor: 'pointer',
                        }}
                        component="div"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <strong>Likes</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'downloads'}
                        direction={sortBy === 'downloads' ? sortOrder : 'asc'}
                        onClick={(e) => handleSort('downloads', e)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: '1 !important',
                            visibility: 'visible !important',
                          },
                          cursor: 'pointer',
                        }}
                        component="div"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <strong>Downloads</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'completion_rate'}
                        direction={sortBy === 'completion_rate' ? sortOrder : 'asc'}
                        onClick={(e) => handleSort('completion_rate', e)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: '1 !important',
                            visibility: 'visible !important',
                          },
                          cursor: 'pointer',
                        }}
                        component="div"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <strong>Completion %</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'returns'}
                        direction={sortBy === 'returns' ? sortOrder : 'asc'}
                        onClick={(e) => handleSort('returns', e)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: '1 !important',
                            visibility: 'visible !important',
                          },
                          cursor: 'pointer',
                        }}
                        component="div"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <strong>Returns</strong>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    analytics
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>{item.category_name || '-'}</TableCell>
                          <TableCell>{item.content_type_name || '-'}</TableCell>
                          <TableCell align="right">{item.total_plays}</TableCell>
                          <TableCell align="right">{item.total_likes}</TableCell>
                          <TableCell align="right">{item.total_downloads}</TableCell>
                          <TableCell align="right">
                            {item.avg_completion_rate !== undefined && item.avg_completion_rate !== null
                              ? `${Number(item.avg_completion_rate).toFixed(1)}%`
                              : '0%'}
                          </TableCell>
                          <TableCell align="right">{item.total_returns}</TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={analytics.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rows per page:"
            />
          </Box>
        </Paper>
      </Box>
    </List>
  );
};

export default ContentAnalyticsList;

